import Bar, { type RenderableText } from '@rio-cloud/rio-uikit/Bar';
import BarChart from '@rio-cloud/rio-uikit/BarChart';
import ChartTooltip from '@rio-cloud/rio-uikit/ChartTooltip';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { InvoiceType } from '@/api/schema';
import { capitalizeFirstLetter, formatCurrencyValue, t } from '@/lib/utils';
import { useLocaleStore } from '@/stores/localeStore';

export type InvoicesChartProps = {
    rows?: InvoiceType[] | undefined;
    currency: string;
};

export const InvoicesChart = ({ rows, currency }: InvoicesChartProps) => {
    const { displayLocale } = useLocaleStore();
    const { locale } = useIntl();
    const monthFormat = new Intl.DateTimeFormat(displayLocale, { month: 'long' }).format;

    const monthsMap = new Map<string, number>();
    rows?.forEach(m => {
        const formattedMonth = m?.issueDate ? monthFormat(new Date(m?.issueDate)) : null;
        if (formattedMonth) {
            const monthEntry = monthsMap.get(formattedMonth);
            if (monthEntry) {
                const sum = m?.totalCost ? m.totalCost + monthEntry : 0;
                monthsMap.set(formattedMonth, sum);
            } else {
                monthsMap.set(formattedMonth, m?.totalCost ?? 0);
            }
        }
    });

    let data: { label: string; costs: number }[] = [];
    for (const entry of monthsMap.entries()) {
        data.push({ label: capitalizeFirstLetter(entry[0]), costs: entry[1] });
    }
    data = data.reverse();

    // biome-ignore lint/correctness/noNestedComponentDefinitions: CustomTooltip is used as a prop
    const CustomTooltip = ({
        active,
        payload,
        label,
    }: {
        active?: boolean;
        payload?: ReadonlyArray<{ value: number | string }>;
        label?: ReactNode;
    }) => {
        if (active && payload?.length) {
            const { value } = formatCurrencyValue(payload[0].value, currency, locale);

            return (
                <div className='panel padding-10'>
                    <span className='display-block'>{label}</span>
                    <span className='text-color-primary'>{`${t('general.costs')}: ${value}`}</span>
                </div>
            );
        }
        return null;
    };

    return (
        <>
            {data && (
                <div className='height-300' style={{ paddingLeft: '80px', paddingRight: '80px' }}>
                    <BarChart
                        data={data}
                        dataKey='label'
                        showYAxis={false}
                        xAxisOptions={{
                            dataKey: 'label',
                        }}
                        tooltip={
                            <ChartTooltip cursor={{ fill: 'var(--gray-lightest)' }} content={CustomTooltip as never} />
                        }
                        bars={[
                            <Bar
                                key='costs'
                                dataKey='costs'
                                label={{
                                    position: 'top',
                                    formatter: (value: RenderableText) =>
                                        formatCurrencyValue(value, currency, locale).value,
                                }}
                            />,
                        ]}
                        margin={{ top: 20 }}
                    />
                </div>
            )}
        </>
    );
};
